import { AppValue } from 'lib/actions/apps'
import { AppEntitlementValue, expandAppEntitlementView } from 'lib/actions/apps/app-entitlement'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { ListReturn, Status } from 'lib/rpc'
import { Collector, unaryListCollectSinglePage } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppEntitlementUserView } from 'pbts/c1/api/app/v1/app_entitlement'
import {
    AppWithGrantStatus,
    RequestCatalogSearchServiceSearchAppsWithGrantStatusRequest,
    RequestCatalogSearchServiceSearchAppsWithGrantStatusResponse,
    RequestCatalogSearchServiceSearchEntitlementsPerAppRequest,
    RequestCatalogSearchServiceSearchEntitlementsPerAppResponse,
    RequestCatalogUserEntitlementPerApp,
    ResourceTypeWithEntitlementCount,
} from 'pbts/c1/api/requestcatalog/v1/requestcatalog'
import { RequestCatalogSearchServiceClient } from 'pbts/c1/api/requestcatalog/v1/requestcatalog.client'
import { Task } from 'pbts/c1/api/task/v1/task'
import { Any } from 'pbts/google/protobuf/any'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { Dispatch } from 'react'
import { RequestCatalogUserEntitlementPerAppValue } from 'redux_toolkit/browse-access'

export enum RequestCatalogSearchAction {
    searchAppsWithGrantStatus = 'RequestCatalogSearchAction.searchAppsWithGrantStatus',
    searchEntitlementsPerApp = 'RequestCatalogSearchAction.searchEntitlementsPerApp',
    searchResourceTypesPerApp = 'RequestCatalogSearchAction.searchResourceTypesPerApp',
    searchAccessEntitlementsPerApp = 'RequestCatalogSearchAction.searchAccessEntitlementsPerApp',
}

export type SearchAppsAction = {
    type: RequestCatalogSearchAction.searchAppsWithGrantStatus
    payload: ListReturn<AppValue>
}

export type SearchEntitlementsPerAppAction = {
    type: RequestCatalogSearchAction.searchEntitlementsPerApp
    payload: ListReturn<RequestCatalogUserEntitlementPerAppValue>
}

export type SearchAccessEntitlementsPerAppAction = {
    type: RequestCatalogSearchAction.searchAccessEntitlementsPerApp
    payload: ListReturn<RequestCatalogUserAccessEntitlementPerAppValue>
}

export type SearchResourceTypesPerAppAction = {
    type: RequestCatalogSearchAction.searchResourceTypesPerApp
    payload: ListReturn<ResourceTypeWithEntitlementCount>
}

export type RequestCatalogSearchActions =
    | SearchAppsAction
    | SearchEntitlementsPerAppAction
    | SearchResourceTypesPerAppAction
    | SearchAccessEntitlementsPerAppAction

export interface RequestCatalogUserAccessEntitlementPerAppValue {
    entitlement?: AppEntitlementValue
    appEntitlementUserBinding?: AppEntitlementUserView
    task?: Task
    isRequestable?: boolean
    id: string
}

export const expandEntitlementPerAppView = (
    msg: RequestCatalogUserEntitlementPerApp | undefined,
    values: Any[],
): RequestCatalogUserEntitlementPerAppValue => {
    const expandedEntitlement = expandAppEntitlementView(msg?.entitlement, values)

    return {
        entitlement: expandedEntitlement,
        appEntitlementUserBinding: msg?.appEntitlementUserBinding,
        task: msg?.task,
        id: msg?.entitlement?.appEntitlement?.id || '',
    }
}

export class RequestCatalogEntitlementsPerAppCollector extends Collector<RequestCatalogUserEntitlementPerAppValue> {
    push(resp: RequestCatalogSearchServiceSearchEntitlementsPerAppResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): RequestCatalogUserEntitlementPerAppValue => {
                return expandEntitlementPerAppView(v, resp.expanded)
            }),
        )
    }
}

export interface AppWithGrantStatusValue extends App {
    grantedAt?: Timestamp
    deprovisionsAt?: Timestamp
}

export const expandApp = (msg: AppWithGrantStatus | undefined, _values: Any[]): AppWithGrantStatusValue => {
    return {
        grantedAt: msg?.binding?.appEntitlementUserBindingCreatedAt,
        deprovisionsAt: msg?.binding?.appEntitlementUserBindingDeprovisionAt,
        ...App.create(msg?.app ?? {}),
    }
}

export class AppCollector extends Collector<AppWithGrantStatusValue> {
    push(resp: RequestCatalogSearchServiceSearchAppsWithGrantStatusResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): AppWithGrantStatusValue => {
                return expandApp(v, [])
            }),
        )
    }
}

export const searchApps = (
    dispatch: Dispatch<RequestCatalogSearchActions>,
    client: RequestCatalogSearchServiceClient | undefined,
    request: RequestCatalogSearchServiceSearchAppsWithGrantStatusRequest,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    request.pageToken = ''
    const collector = new AppCollector()
    return unaryListCollectSinglePage<
        RequestCatalogSearchServiceSearchAppsWithGrantStatusRequest,
        RequestCatalogSearchServiceSearchAppsWithGrantStatusResponse
    >(
        request,
        client.searchAppsWithGrantStatus.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: RequestCatalogSearchAction.searchAppsWithGrantStatus,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                            metadata: payload.metadata,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: RequestCatalogSearchAction.searchAppsWithGrantStatus,
                        payload,
                    })
            }
        },
    )
}

export const searchEntitlementsPerApp = (
    dispatch: Dispatch<RequestCatalogSearchActions>,
    client: RequestCatalogSearchServiceClient | undefined,
    request: RequestCatalogSearchServiceSearchEntitlementsPerAppRequest,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    request.pageToken = ''
    const collector = new RequestCatalogEntitlementsPerAppCollector()
    return unaryListCollectSinglePage<
        RequestCatalogSearchServiceSearchEntitlementsPerAppRequest,
        RequestCatalogSearchServiceSearchEntitlementsPerAppResponse
    >(
        request,
        client.searchEntitlementsPerApp.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: RequestCatalogSearchAction.searchEntitlementsPerApp,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                            metadata: payload.metadata,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: RequestCatalogSearchAction.searchEntitlementsPerApp,
                        payload,
                    })
            }
        },
    )
}
